/**
 * AK-938 — карточка схемы опыта (low-fi 2·А): общие данные, факторы, варианты с
 * препаратами, шаблон осмотров и опыты, заложенные по схеме.
 */
import { useNavigate } from '@tanstack/react-router';
import { Archive, MapPinPlus, Pencil } from 'lucide-react';
import { AppShell } from '@/components/layout/AppShell';
import { PageHeader } from '@/components/layout/PageHeader';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { Stack } from '@/components/ui/stack';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { TrialStatusBadge } from '@/components/science/TrialStatusBadge';
import { ApiError } from '@/lib/api';
import { formatRuDateNumeric } from '@/lib/dateFmt';
import { toast } from '@/lib/toast';
import { useArchiveTrialScheme, useTrialScheme, useTrials } from '@/lib/science/api';
import {
  CHECK_KIND_LABELS,
  FACTOR_LABELS,
  ROLE_LABELS,
  variantMarks,
  variantTreatment,
} from '@/lib/science/labels';
import { useScienceSeasonLensHost } from '@/lib/science/seasonLens';

function Row({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div className="flex min-h-10 items-center justify-between gap-4 border-b border-border py-2 text-sm leading-6 last:border-0">
      <span className="text-muted-foreground">{label}</span>
      <span className="text-right">{value}</span>
    </div>
  );
}

export function TrialSchemePage({ schemeId }: { schemeId: string }) {
  useScienceSeasonLensHost();
  const navigate = useNavigate();
  const scheme = useTrialScheme(schemeId);
  const trials = useTrials({ perPage: 100 });
  const archive = useArchiveTrialScheme();
  const s = scheme.data;

  const byScheme = (trials.data?.items ?? []).filter((t) => t.scheme_id === schemeId);
  const inWork = byScheme.filter((t) => t.status === 'laid' || t.status === 'planned').length;
  const withYield = byScheme.filter((t) => t.status === 'closed_yield').length;
  const incomplete = byScheme.filter((t) => t.status === 'closed_incomplete').length;

  async function toArchive() {
    try {
      await archive.mutateAsync(schemeId);
      toast.success('Схема в архиве. Заложенные по ней опыты остаются.');
    } catch (e) {
      toast.error(e instanceof ApiError ? e.detail : 'Не удалось убрать схему в архив');
    }
  }

  return (
    <AppShell>
      <Stack gap={6}>
        <PageHeader
          title={s?.name ?? 'Схема опыта'}
          description={
            s
              ? `Схема опыта · сезон ${s.season_year} · создана ${formatRuDateNumeric(s.created_at)}${s.version > 1 ? ` · версия ${s.version}` : ''}`
              : undefined
          }
          breadcrumbs={[
            { label: 'Наука', to: '/science' },
            { label: 'Схемы', to: '/science', search: { tab: 'schemes' } },
            { label: s?.name ?? 'Схема' },
          ]}
          actions={
            s && s.status !== 'archived' ? (
              <div className="flex flex-wrap gap-2">
                <Button variant="ghost" onClick={() => void toArchive()} disabled={archive.isPending}>
                  <Archive className="h-4 w-4" />В архив
                </Button>
                <Button
                  variant="outline"
                  onClick={() =>
                    void navigate({ to: '/science/schemes/$schemeId/edit', params: { schemeId } })
                  }
                >
                  <Pencil className="h-4 w-4" />
                  Изменить
                </Button>
                <Button
                  disabled={s.status !== 'active'}
                  title={s.status !== 'active' ? 'Сначала сохраните схему как действующую' : undefined}
                  onClick={() => void navigate({ to: '/science/place', search: { scheme: schemeId } })}
                >
                  <MapPinPlus className="h-4 w-4" />
                  Заложить опыт
                </Button>
              </div>
            ) : undefined
          }
        />

        {scheme.isLoading ? (
          <Skeleton className="h-64 w-full" />
        ) : scheme.isError || !s ? (
          <Alert variant="destructive">
            <AlertDescription>
              Схему открыть не удалось{scheme.error ? `: ${scheme.error.message}` : ''}
            </AlertDescription>
          </Alert>
        ) : (
          <>
            {s.status === 'draft' && (
              <Alert>
                <AlertDescription>
                  Черновик: по нему ещё нельзя заложить опыт. Откройте «Изменить» и сохраните схему.
                </AlertDescription>
              </Alert>
            )}
            <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
              <Card>
                <CardHeader className="p-4 pb-3">
                  <CardTitle className="text-base">Общие данные</CardTitle>
                </CardHeader>
                <CardContent className="p-4 pt-0">
                  <Row label="Сезон" value={s.season_year} />
                  <Row label="Создана" value={formatRuDateNumeric(s.created_at)} />
                  <Row label="Комментарий" value={s.comment || '—'} />
                </CardContent>
              </Card>
              <Card>
                <CardHeader className="p-4 pb-3">
                  <CardTitle className="text-base">Факторы</CardTitle>
                  <p className="text-sm text-muted-foreground">что сравниваем</p>
                </CardHeader>
                <CardContent className="flex flex-wrap gap-2 p-4 pt-0">
                  {s.factors.map((f) => (
                    <Badge key={f} variant="secondary">
                      {FACTOR_LABELS[f]}
                    </Badge>
                  ))}
                </CardContent>
              </Card>
              <Card>
                <CardHeader className="p-4 pb-3">
                  <CardTitle className="text-base">Опыты по схеме</CardTitle>
                </CardHeader>
                <CardContent className="p-4 pt-0">
                  <Row label="В работе" value={inWork} />
                  <Row label="Закрыто с урожайностью" value={withYield} />
                  <Row label="Закрыто недоделанными" value={incomplete} />
                </CardContent>
              </Card>
            </div>

            <Card>
              <CardHeader className="p-4 pb-3">
                <CardTitle className="text-base">Варианты</CardTitle>
                <p className="text-sm text-muted-foreground">
                  контроль — ровно один · эталон — 0 или 1 · опытных — 1 и больше
                </p>
              </CardHeader>
              <CardContent className="overflow-x-auto p-4 pt-0">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Вариант</TableHead>
                      <TableHead>Роль</TableHead>
                      <TableHead>Что применяем · доза · срок</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {s.variants.map((v, i) => (
                      <TableRow key={v.id}>
                        <TableCell className="whitespace-nowrap">
                          {variantMarks(s.variants.map((x) => x.role))[i]} · {v.name}
                        </TableCell>
                        <TableCell>
                          <Badge variant="outline">{ROLE_LABELS[v.role].toLowerCase()}</Badge>
                        </TableCell>
                        <TableCell>{variantTreatment(v.inputs, v.note)}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="p-4 pb-3">
                <CardTitle className="text-base">Шаблон осмотров</CardTitle>
                <p className="text-sm text-muted-foreground">
                  по нему заполняются осмотры в каждом опыте по схеме
                </p>
              </CardHeader>
              <CardContent className="overflow-x-auto p-4 pt-0">
                {s.check_items.length ? (
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Что смотрим</TableHead>
                        <TableHead>Как записываем</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {s.check_items.map((c) => (
                        <TableRow key={c.id ?? c.label}>
                          <TableCell>{c.label}</TableCell>
                          <TableCell>
                            {CHECK_KIND_LABELS[c.value_kind]}
                            {c.choices?.length ? `: ${c.choices.join(', ')}` : ''}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                ) : (
                  <p className="text-sm text-muted-foreground">Шаблон не задан — осмотры свободные.</p>
                )}
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="p-4 pb-3">
                <CardTitle className="text-base">Опыты по схеме</CardTitle>
              </CardHeader>
              <CardContent className="overflow-x-auto p-4 pt-0">
                {byScheme.length ? (
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Сезон</TableHead>
                        <TableHead>Хозяйство</TableHead>
                        <TableHead>Поле</TableHead>
                        <TableHead>Заложен</TableHead>
                        <TableHead>Статус</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {byScheme.map((t) => (
                        <TableRow key={`${t.field_tenant_id}:${t.field_trial_id}`}>
                          <TableCell>{t.season_year}</TableCell>
                          <TableCell>{t.farm_label ?? '—'}</TableCell>
                          <TableCell>{t.access === 'closed' ? t.label : t.field_label ?? '—'}</TableCell>
                          <TableCell>{t.laid_at ? formatRuDateNumeric(t.laid_at) : '—'}</TableCell>
                          <TableCell>
                            <TrialStatusBadge status={t.status} label={t.status_label} />
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                ) : (
                  <p className="text-sm text-muted-foreground">По схеме ещё не заложено ни одного опыта.</p>
                )}
              </CardContent>
            </Card>
          </>
        )}
      </Stack>
    </AppShell>
  );
}
